import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
}                                 from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA }        from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  take,
}                                 from 'rxjs';
import {
  fileIcon,
  needsBrandColor,
}                                 from '~domain/enums';
import {
  RbDocument,
  UserSettings,
}                                 from '~domain/types';
import {
  DialogButton,
  DialogConfig,
}                                 from '~shared/components';
import { RbDialogForm }           from '~shared/directives';
import { ValidatorService }       from '../../core/validators';
import { MainService }            from '../../main/main.service';
import { SelectClientFormData }   from '../select-client/select-client.component';
import { AssignDocumentService }  from './assign-document.service';

export type AssignDocumentRequest = {
  documents: RbDocument[],
  predictedClientId?: string,
  userSettings: UserSettings,
};

@Component({
  selector: 'rb-assign-document-dialog',
  templateUrl: './assign-document-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AssignDocumentDialogComponent extends RbDialogForm implements OnInit {
  assignDocumentType$ = this.assignDocumentService.assignDocumentType$;
  assignDocumentUrl$ = this.assignDocumentService.assignDocumentUrl$;
  assignDocumentClients$ = this.assignDocumentService.assignDocumentClients$;
  assignDocumentIsLoading$ = this.assignDocumentService.assignDocumentIsLoading$;
  override form = this.fb.group({
    client: this.fb.control<SelectClientFormData>(
      { clientId: this.request.predictedClientId ?? (this.documents.length === 1 ? this.documents[0].client?.id ?? '' : '') },
    ),
  });
  dialogConfig: DialogConfig = {
    title: `APP.DOCUMENT.ASSIGN_DOCUMENT.DIALOG.TITLE${this.translationKeySuffix}`,
    closeable: true,
    maximizable: this.documents.length === 1,
    errorKey$: this.errorKey$,
    errorContext: 'APP.DOCUMENT.ASSIGN_DOCUMENT.DIALOG.ERROR',
    buttons: [
      {
        id: DialogButton.Cancel,
        label: 'APP.BUTTON.CANCEL.LABEL',
        icon: 'cross',
        style: 'secondary',
      },
      {
        id: DialogButton.OK,
        label: 'APP.BUTTON.CONFIRM.LABEL',
        icon: 'check',
        style: 'primary',
        autoClose: false,
        disabled$: this.submitDisabled$,
        isLoading$: this.assignDocumentIsLoading$,
      },
    ],
  };
  dialogMaximized: boolean = false;
  protected readonly needsBrandColor = needsBrandColor;
  protected readonly fileIcon = fileIcon;
  private readonly documentNameEditingErrorKey = 'document_name_editing';
  private readonly documentNameEditing$ = new BehaviorSubject(false);

  constructor(
    protected override readonly fb: NonNullableFormBuilder,
    @Inject(MAT_DIALOG_DATA) public request: AssignDocumentRequest,
    private readonly assignDocumentService: AssignDocumentService,
    private readonly mainService: MainService,
    protected override readonly validatorService: ValidatorService,
  ) {
    super(fb, validatorService);
  }

  get assignFormRadioGroupHeight() {
    return this.dialogMaximized ? 'calc(100vh - 31.7rem)' : 'min(56.6rem, calc(100vh - 31.7rem))';
  }

  get docViewerMaxHeight() {
    return this.dialogMaximized ? 'calc(100vh - 18.2rem)' : 'min(70.1rem, calc(100vh - 18.2rem))';
  }

  get translationKeySuffix() {
    return this.documents.length === 1 ? '_SINGLE' : '_MULTI';
  }

  get document() {
    return this.documents[0] ?? null;
  }

  override get errorKey$() {
    return combineLatest([super.errorKey$, defer(() => this.documentNameEditing$)]).pipe(
      map(([errorKey, documentNameEditing]) => errorKey ?? (documentNameEditing ? this.documentNameEditingErrorKey : null)),
      distinctUntilChanged(),
    );
  }

  override get submitDisabled$() {
    return combineLatest([super.submitDisabled$, defer(() => this.documentNameEditing$)]).pipe(
      map(([submitDisabled, documentNameEditing]) => submitDisabled || documentNameEditing),
      distinctUntilChanged(),
    );
  }

  private get documents() {
    return this.request.documents ?? [];
  }

  private get documentNameEditing() {
    return this.documentNameEditing$.value;
  }

  ngOnInit() {
    if (this.documents.length === 1 && this.request.userSettings?.dialogMaximized) {
      this.dialogMaximized = true;
    }
  }

  onDocumentNameRenamingChange(renaming: boolean) {
    this.documentNameEditing$.next(renaming);
    this.form.updateValueAndValidity();
  }

  onDialogButtonClick(dialogButton: DialogButton) {
    if (dialogButton === DialogButton.OK && this.form.valid && !this.documentNameEditing) {
      this.assignDocumentService.assignDocuments(
        this.documents.map(d => d.id),
        this.form.controls.client.value.clientId,
      );
    }
    if (dialogButton === DialogButton.Cancel) {
      this.onClose();
    }
  }

  onClose() {
    this.assignDocumentUrl$.pipe(
      take(1),
      filter(Boolean),
    ).subscribe(() => {
      this.assignDocumentService.clearAssignDocument();
    });
    this.assignDocumentService.reloadPage();
  }

  onMaximizedChange(newValue: boolean) {
    this.mainService.setUserSettings({ dialogMaximized: newValue });
  }
}
